import { SSOSchemesService } from "@app/sso-schemes";
import { useContext } from "react";
import { AccountContext } from "../AccountContext";
import { SSOAction } from "../actions";
import { SSO, SSOStatus, toSSO, toSSOConfig } from "../models";

interface UseSsoReturnType {
  beginMetadataDownload: () => void;
  deleteSSO: () => Promise<void>;
  endMetadataDownload: () => void;
  hasSSO: boolean;
  hasStatus: (statuses: SSOStatus[]) => boolean;
  loadSSO: () => Promise<void>;
  saveSSO: (create?: boolean) => Promise<void>;
  sso: SSO;
  status: SSOStatus;
  toggleIsEnabled: () => Promise<void>;
  updateSSO: (propertyKey: keyof SSO, propertyValue: unknown) => void;
  validateCertificate: (certBase64: string) => Promise<boolean>;
}

export function useSSO(): UseSsoReturnType {
  const { clientId, ssoDispatch, ssoState } = useContext(AccountContext);
  const ssoService = new SSOSchemesService();

  const hasSSO = !!ssoState.sso && !!ssoState.sso.schemeName;
  const hasStatus = (statuses: SSOStatus[]) => {
    let status = SSOStatus.None;
    statuses.forEach((s) => {
      status |= s;
    });
    return !!(ssoState.status & status);
  };

  const beginMetadataDownload = () => {
    ssoDispatch({ type: SSOAction.MetadataDownloadBegin });
  };

  const deleteSSO = async () => {
    ssoDispatch({ type: SSOAction.DeleteSSO });
    try {
      await ssoService.delete(ssoState.sso.schemeName);
      ssoDispatch({ type: SSOAction.DeleteSSOSuccess });
    } catch (error) {
      ssoDispatch({ error, type: SSOAction.DeleteSSOFailure });
    }
  };

  const endMetadataDownload = () => {
    ssoDispatch({ type: SSOAction.MetadataDownloadEnd });
  };

  const loadSSO = async () => {
    ssoDispatch({ clientId, type: SSOAction.LoadSSO });
    try {
      const ssoConfig = await ssoService.get(clientId);
      if (ssoConfig) {
        const sso = toSSO(ssoConfig);
        ssoDispatch({ sso, type: SSOAction.LoadSSOSuccess });
      }
    } catch (error) {
      ssoDispatch({ error, type: SSOAction.LoadSSOFailure });
    }
  };

  const saveSSO = async (create?: boolean) => {
    ssoDispatch({ type: SSOAction.SaveSSO });
    try {
      const ssoConfig = toSSOConfig(ssoState.sso, clientId);
      if (create) {
        const result = await ssoService.post(ssoConfig);
        const sso = toSSO(result);
        ssoDispatch({ sso, type: SSOAction.SaveSSOSuccess });
      } else {
        await ssoService.patch(ssoConfig);
        ssoDispatch({ sso: ssoState.sso, type: SSOAction.SaveSSOSuccess });
      }
    } catch (error) {
      ssoDispatch({ error, type: SSOAction.SaveSSOFailure });
      return;
    }
  };

  const updateSSO = (propertyKey: keyof SSO, propertyValue: boolean) => {
    ssoDispatch({ propertyKey, propertyValue, type: SSOAction.UpdateSSO });
  };

  const toggleIsEnabled = async () => {
    const updatedSSO = { ...ssoState.sso, isEnabled: !ssoState.sso.isEnabled };
    updateSSO("isEnabled", updatedSSO.isEnabled);
  };

  const validateCertificate = async (certBase64: string) => {
    try {
      return await ssoService.validateSigningCertificate(certBase64);
    } catch (error) {
      return false;
    }
  }

  return {
    beginMetadataDownload,
    deleteSSO,
    endMetadataDownload,
    hasSSO,
    hasStatus,
    loadSSO,
    saveSSO,
    sso: ssoState.sso,
    status: ssoState.status,
    toggleIsEnabled,
    updateSSO,
    validateCertificate,
  };
}
